import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import ImageUploader from "@/components/admin/ImageUploader";
import VideoUploader from "@/components/admin/VideoUploader";
import { Field, TextInput, TextArea, Select, Toggle } from "@/components/admin/FormControls";
import { Save, RotateCcw, ExternalLink, Plus, Trash2, Loader2 } from "lucide-react";

export default function AdminHome() {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [toast, setToast] = useState("");

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await api.get("/admin/site/home");
            setContent(data.content);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            const { data } = await api.put("/admin/site/home", { content });
            setContent(data.content);
            setToast("Configurações salvas.");
            setTimeout(() => setToast(""), 2500);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        if (!window.confirm("Restaurar valores padrão da home? Suas edições serão perdidas.")) return;
        setSaving(true);
        try {
            const { data } = await api.post("/admin/site/home/reset");
            setContent(data.content);
            setToast("Conteúdo restaurado ao padrão.");
            setTimeout(() => setToast(""), 2500);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const setPath = (path, value) => {
        setContent(prev => {
            const next = structuredClone(prev || {});
            const keys = path.split(".");
            let obj = next;
            for (let i = 0; i < keys.length - 1; i++) {
                obj[keys[i]] = obj[keys[i]] ?? {};
                obj = obj[keys[i]];
            }
            obj[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const updateStat = (i, key, val) => {
        setContent(prev => {
            const next = structuredClone(prev);
            next.hero = next.hero || {};
            next.hero.stats = next.hero.stats || [];
            next.hero.stats[i] = { ...(next.hero.stats[i] || {}), [key]: val };
            return next;
        });
    };
    const addStat = () => {
        setContent(prev => {
            const next = structuredClone(prev);
            next.hero = next.hero || {};
            next.hero.stats = [...(next.hero.stats || []), { label: "Nova métrica", value: "0" }];
            return next;
        });
    };
    const removeStat = (i) => {
        setContent(prev => {
            const next = structuredClone(prev);
            next.hero.stats = (next.hero.stats || []).filter((_, idx) => idx !== i);
            return next;
        });
    };

    if (loading || !content) {
        return (
            <AdminLayout title="Home" eyebrow="Controle de Conteúdo">
                <div className="flex items-center gap-2 text-[#5C5C5C] font-body text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
                </div>
            </AdminLayout>
        );
    }

    const hero = content.hero || {};
    const featured = content.featured || {};
    const freeSec = content.free_section || {};
    const manifesto = content.manifesto || {};
    const stats = hero.stats || [];

    const actions = (
        <>
            <a href="/home" target="_blank" rel="noreferrer" className="btn-ghost" data-testid="admin-home-preview">
                <ExternalLink className="w-4 h-4" /> Ver página
            </a>
            <button onClick={reset} disabled={saving} className="btn-ghost" data-testid="admin-home-reset">
                <RotateCcw className="w-4 h-4" /> Restaurar padrão
            </button>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="admin-home-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? "Salvando…" : "Salvar alterações"}
            </button>
        </>
    );

    return (
        <AdminLayout title="Home" eyebrow="Controle de Conteúdo" actions={actions}>
            {error && (
                <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm font-body" data-testid="admin-home-error">
                    {error}
                </div>
            )}
            {toast && (
                <div className="mb-6 p-3 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-700 text-sm font-body" data-testid="admin-home-toast">
                    {toast}
                </div>
            )}

            {/* HERO */}
            <section className="bg-white rounded-2xl border border-black/5 p-7">
                <div className="overline text-[#5C5C5C]">Seção 1</div>
                <h2 className="font-display text-2xl mt-1">Banner da hero</h2>
                <p className="font-body text-sm text-[#5C5C5C] mt-1">Vídeo de fundo, título editorial, copy e CTAs principais.</p>

                <div className="grid lg:grid-cols-2 gap-6 mt-6">
                    <div className="space-y-4">
                        <Field label="Overline (etiqueta superior)">
                            <TextInput value={hero.overline} onChange={(v) => setPath("hero.overline", v)} testId="home-hero-overline" />
                        </Field>
                        <Field label="Título · linha 1 (texto normal)">
                            <TextInput value={hero.title_pre} onChange={(v) => setPath("hero.title_pre", v)} testId="home-hero-title-pre" />
                        </Field>
                        <Field label="Título · linha 2 (em itálico, com gradiente)">
                            <TextInput value={hero.title_em} onChange={(v) => setPath("hero.title_em", v)} testId="home-hero-title-em" />
                        </Field>
                        <Field label="Título · linha 3 (texto normal)">
                            <TextInput value={hero.title_post} onChange={(v) => setPath("hero.title_post", v)} testId="home-hero-title-post" />
                        </Field>
                        <Field label="Subtítulo / descrição">
                            <TextArea value={hero.subtitle} onChange={(v) => setPath("hero.subtitle", v)} rows={4} testId="home-hero-subtitle" />
                        </Field>
                    </div>

                    <div className="space-y-4">
                        <Field label="Imagem de fundo (poster)" hint="É exibida enquanto o vídeo carrega — ideal: 2000×1200 px.">
                            <ImageUploader
                                value={hero.poster_url}
                                onChange={(url) => setPath("hero.poster_url", url)}
                                aspect="aspect-[16/9]"
                                testId="home-hero-poster"
                            />
                        </Field>
                        <Field label="URL do vídeo de fundo" hint="MP4 público (Vimeo direct, S3, customer-assets). Deixe vazio para usar apenas a imagem.">
                            <VideoUploader
                                value={hero.video_url}
                                onChange={(url) => setPath("hero.video_url", url)}
                                testId="home-hero-video"
                                hint="Loop automático, sem som — funciona melhor com vídeos curtos (10–30s)."
                            />
                        </Field>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-black/5">
                    <Field label="Botão primário · texto">
                        <TextInput value={hero.cta_primary_label} onChange={(v) => setPath("hero.cta_primary_label", v)} testId="home-cta1-label" />
                    </Field>
                    <Field label="Botão primário · link (path interno)">
                        <TextInput value={hero.cta_primary_href} onChange={(v) => setPath("hero.cta_primary_href", v)} placeholder="/cadastro" testId="home-cta1-href" />
                    </Field>
                    <Field label="Botão secundário · texto">
                        <TextInput value={hero.cta_secondary_label} onChange={(v) => setPath("hero.cta_secondary_label", v)} testId="home-cta2-label" />
                    </Field>
                    <Field label="Botão secundário · link">
                        <TextInput value={hero.cta_secondary_href} onChange={(v) => setPath("hero.cta_secondary_href", v)} placeholder="/jornadas" testId="home-cta2-href" />
                    </Field>
                </div>

                {/* Stats */}
                <div className="mt-8 pt-6 border-t border-black/5">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="overline text-[#5C5C5C]">Indicadores</div>
                            <h3 className="font-display text-lg">Métricas exibidas abaixo dos CTAs</h3>
                        </div>
                        <button onClick={addStat} className="btn-ghost" data-testid="home-stat-add">
                            <Plus className="w-4 h-4" /> Adicionar
                        </button>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                        {stats.map((s, i) => (
                            <div key={i} className="p-4 rounded-xl bg-[#FAFAF7] border border-black/5 space-y-3" data-testid={`home-stat-${i}`}>
                                <Field label="Valor">
                                    <TextInput value={s.value} onChange={(v) => updateStat(i, "value", v)} placeholder="+12" testId={`home-stat-${i}-value`} />
                                </Field>
                                <Field label="Rótulo">
                                    <TextInput value={s.label} onChange={(v) => updateStat(i, "label", v)} placeholder="Jornadas guiadas" testId={`home-stat-${i}-label`} />
                                </Field>
                                <button onClick={() => removeStat(i)} className="text-xs text-red-600 inline-flex items-center gap-1 font-display" data-testid={`home-stat-${i}-remove`}>
                                    <Trash2 className="w-3 h-3" /> Remover
                                </button>
                            </div>
                        ))}
                        {stats.length === 0 && (
                            <div className="p-4 rounded-xl border border-dashed border-black/15 text-[#5C5C5C] font-body text-sm">
                                Nenhuma métrica. Use "Adicionar" para criar.
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* FEATURED */}
            <section className="bg-white rounded-2xl border border-black/5 p-7 mt-6">
                <div className="overline text-[#5C5C5C]">Seção 2</div>
                <h2 className="font-display text-2xl mt-1">Jornadas em destaque</h2>
                <p className="font-body text-sm text-[#5C5C5C] mt-1">
                    Define o título e a quantidade de cards por linha. As jornadas marcadas como "destaque" em
                    <a href="/admin/jornadas" className="underline ml-1">Jornadas</a> aparecem aqui automaticamente.
                </p>

                <div className="grid sm:grid-cols-2 gap-4 mt-6">
                    <Field label="Eyebrow">
                        <TextInput value={featured.eyebrow} onChange={(v) => setPath("featured.eyebrow", v)} testId="home-featured-eyebrow" />
                    </Field>
                    <Field label="Título da seção">
                        <TextInput value={featured.title} onChange={(v) => setPath("featured.title", v)} testId="home-featured-title" />
                    </Field>
                    <Field label="Cards por linha" hint="1 a 6. Quando há mais jornadas, elas quebram para a próxima linha automaticamente.">
                        <Select
                            value={String(featured.columns ?? 4)}
                            onChange={(v) => setPath("featured.columns", Number(v))}
                            options={[1,2,3,4,5,6].map(n => ({ value: String(n), label: `${n} ${n === 1 ? "coluna" : "colunas"}` }))}
                            testId="home-featured-columns"
                        />
                    </Field>
                </div>
            </section>

            {/* FREE SECTION */}
            <section className="bg-white rounded-2xl border border-black/5 p-7 mt-6">
                <div className="overline text-[#5C5C5C]">Seção 3</div>
                <h2 className="font-display text-2xl mt-1">Comece gratuitamente</h2>
                <p className="font-body text-sm text-[#5C5C5C] mt-1">Carrossel das jornadas marcadas como gratuitas.</p>
                <div className="grid sm:grid-cols-3 gap-4 mt-6">
                    <Field label="Exibir esta seção">
                        <Toggle checked={freeSec.enabled !== false} onChange={(v) => setPath("free_section.enabled", v)} label={freeSec.enabled !== false ? "Visível" : "Oculta"} testId="home-free-enabled" />
                    </Field>
                    <Field label="Eyebrow">
                        <TextInput value={freeSec.eyebrow} onChange={(v) => setPath("free_section.eyebrow", v)} testId="home-free-eyebrow" />
                    </Field>
                    <Field label="Título">
                        <TextInput value={freeSec.title} onChange={(v) => setPath("free_section.title", v)} testId="home-free-title" />
                    </Field>
                </div>
            </section>

            {/* MANIFESTO */}
            <section className="bg-white rounded-2xl border border-black/5 p-7 mt-6">
                <div className="overline text-[#5C5C5C]">Seção 4</div>
                <h2 className="font-display text-2xl mt-1">Manifesto</h2>
                <p className="font-body text-sm text-[#5C5C5C] mt-1">Bloco escuro com vídeo de fundo e citação editorial.</p>
                <div className="grid lg:grid-cols-2 gap-6 mt-6">
                    <div className="space-y-4">
                        <Field label="Exibir esta seção">
                            <Toggle checked={manifesto.enabled !== false} onChange={(v) => setPath("manifesto.enabled", v)} label={manifesto.enabled !== false ? "Visível" : "Oculta"} testId="home-manifesto-enabled" />
                        </Field>
                        <Field label="Citação" hint="Use aspas tipográficas (“ ”) para um visual mais editorial.">
                            <TextArea value={manifesto.quote} onChange={(v) => setPath("manifesto.quote", v)} rows={4} testId="home-manifesto-quote" />
                        </Field>
                        <Field label="Assinatura">
                            <TextArea value={manifesto.tagline} onChange={(v) => setPath("manifesto.tagline", v)} rows={2} testId="home-manifesto-tagline" />
                        </Field>
                    </div>
                    <div className="space-y-4">
                        <Field label="Vídeo de fundo">
                            <VideoUploader
                                value={manifesto.video_url}
                                onChange={(url) => setPath("manifesto.video_url", url)}
                                testId="home-manifesto-video"
                                hint="Aparece em loop, com escurecimento por cima — destaque a citação."
                            />
                        </Field>
                        <Field label="Botão · texto">
                            <TextInput value={manifesto.cta_label} onChange={(v) => setPath("manifesto.cta_label", v)} testId="home-manifesto-cta-label" />
                        </Field>
                        <Field label="Botão · link">
                            <TextInput value={manifesto.cta_href} onChange={(v) => setPath("manifesto.cta_href", v)} placeholder="/cadastro" testId="home-manifesto-cta-href" />
                        </Field>
                    </div>
                </div>
            </section>

            {/* Sticky bottom action */}
            <div className="sticky bottom-4 mt-8 flex justify-end">
                <button onClick={save} disabled={saving} className="btn-primary shadow-2xl shadow-black/15" data-testid="admin-home-save-bottom">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? "Salvando…" : "Salvar alterações"}
                </button>
            </div>
        </AdminLayout>
    );
}
